import { useState, useRef, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import {
  ArrowLeft,
  Brain,
  Fish,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  XCircle,
  Send,
  Loader2,
  Bot,
  User,
  Image as ImageIcon,
  MapPin,
  Calendar,
  Stethoscope,
  Activity,
  Lightbulb,
  Shield,
  Clock,
  ChevronDown,
  ChevronUp,
  Target,
  Pill,
} from 'lucide-react'
import { useAIAnalysis, useFollowUpStream } from '../hooks'
import { RateLimitBadge, RecommendationsList, Timeline } from '../components'
import type { Severity } from '../types'
import { ANOMALY_CATEGORY_LABELS, TRIGGER_RECORD_TYPE_LABELS } from '../types'
import { apiClient } from '@/shared/api/client'
import { imageApi } from '@/shared/api/imageApi'
import { queryKeys } from '@/shared/api/queryClient'
import { ImageGallery } from '@/shared/components/ui/ImageGallery'
import { useImageStore } from '@/shared/stores/imageStore'

// Severity badge config
const SEVERITY_CONFIG: Record<Severity, { bg: string; border: string; text: string; icon: React.ElementType; label: string; description: string }> = {
  none: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    icon: CheckCircle,
    label: 'All Clear',
    description: 'No issues detected. Fish appear healthy.'
  },
  low: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    icon: CheckCircle,
    label: 'Low Severity',
    description: 'This condition is manageable and not immediately threatening.'
  },
  medium: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-700',
    icon: AlertCircle,
    label: 'Medium Severity',
    description: 'Action recommended within the next few days.'
  },
  high: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-700',
    icon: AlertTriangle,
    label: 'High Severity',
    description: 'Prompt attention required to prevent spread or worsening.'
  },
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    icon: XCircle,
    label: 'Critical',
    description: 'Immediate action required. This could result in significant losses if not addressed urgently.'
  },
}

// Helper to check if content looks like JSON
function looksLikeJSON(content: string): boolean {
  const trimmed = content.trim()
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) return true
  if (trimmed.includes('"summary":') || trimmed.includes('"recommendations":')) return true
  return false
}

// Extract key information from the AI summary for the quick summary abstract
function extractAbstract(summary: string): {
  diagnoses: Array<{ name: string; confidence: string; evidence: string[] }>;
  treatment: string[];
} {
  const diagnoses: Array<{ name: string; confidence: string; evidence: string[] }> = []
  const treatment: string[] = []

  // Extract differential diagnoses section
  const diagnosisMatch = summary.match(/6\.\s*DIFFERENTIAL DIAGNOSIS[\s\S]*?(?=###\s*7|$)/i)
  if (diagnosisMatch) {
    const diagSection = diagnosisMatch[0]
    const diagBlocks = diagSection.split(/\d+\.\s*\*\*/).filter(Boolean)

    for (const block of diagBlocks) {
      const nameMatch = block.match(/^([^*]+)\*\*\s*[-–]\s*(\d+%?\s*[Cc]onfidence|\d+%)/)
      if (nameMatch) {
        const evidence: string[] = []
        const supportMatch = block.match(/Supporting[^:]*:\s*([^\n]+)/i)
        if (supportMatch) {
          evidence.push(supportMatch[1].trim())
        }
        diagnoses.push({
          name: nameMatch[1].trim(),
          confidence: nameMatch[2].replace(/\s*[Cc]onfidence/i, '').trim(),
          evidence
        })
      }
    }
  }

  // Extract treatment section
  const treatmentMatch = summary.match(/7\.\s*TREATMENT RECOMMENDATIONS[\s\S]*?(?=###\s*8|$)/i)
  if (treatmentMatch) {
    const immediateMatch = treatmentMatch[0].match(/Immediate\s*[Aa]ction[^:]*:\s*([^\n]+)/i)
    if (immediateMatch) {
      treatment.push(immediateMatch[1].trim())
    }
    const treatmentOptMatch = treatmentMatch[0].match(/(?:Antibiotics|Treatment)[^:]*:\s*([^\n]+)/i)
    if (treatmentOptMatch && treatment.length < 2) {
      treatment.push(treatmentOptMatch[1].trim())
    }
    const vetMatch = treatmentMatch[0].match(/[Cc]onsult[^:]*[Vv]eterinarian[^:]*:\s*([^\n]+)/i)
    if (vetMatch && treatment.length < 3) {
      treatment.push('Consult veterinarian ' + vetMatch[1].trim().toLowerCase())
    }
  }

  return { diagnoses, treatment }
}

export function AIAnalysisDetailPage() {
  const { farmId, analysisId } = useParams<{ farmId: string; analysisId: string }>()
  const [followUpQuestion, setFollowUpQuestion] = useState('')
  const [showGallery, setShowGallery] = useState(false)
  const [galleryStartIndex, setGalleryStartIndex] = useState(0)
  const [showDetails, setShowDetails] = useState(false)
  const [showOriginalContext, setShowOriginalContext] = useState(false)
  const chatContainerRef = useRef<HTMLDivElement>(null)

  const setImages = useImageStore((state) => state.setImages)

  const { data: analysis, isLoading, error, refetch } = useAIAnalysis(analysisId)

  const { data: pondData } = useQuery({
    queryKey: queryKeys.ponds.detail(farmId || '', analysis?.pondId || ''),
    queryFn: async () => {
      const response = await apiClient.get(`/farms/${farmId}/ponds/${analysis?.pondId}`)
      return response.data
    },
    enabled: !!analysis?.pondId && !!farmId,
  })

  // Fetch signed URLs for images attached to this analysis
  const { data: signedUrlsResponse, isLoading: isImagesLoading } = useQuery({
    queryKey: queryKeys.images.signedUrls(analysis?.imageIds || []),
    queryFn: () => imageApi.getSignedUrls(analysis?.imageIds || []),
    enabled: !!analysis?.imageIds && analysis.imageIds.length > 0,
  })

  // Update Zustand cache when signed URLs are fetched
  useEffect(() => {
    if (signedUrlsResponse?.images) {
      setImages(signedUrlsResponse.images)
    }
  }, [signedUrlsResponse, setImages])

  const pondName = pondData?.data?.name || 'Unknown Pond'
  const followUpStream = useFollowUpStream()
  const analysisImages = signedUrlsResponse?.images || []

  // Separate initial analysis context from follow-up conversation
  const allMessages = analysis?.conversation || []

  // Initial AI chain of thought - only show the AI's first response, not the system-generated prompts
  const initialMessages = allMessages.filter((msg) => {
    if (msg.role === 'system') return false
    if (msg.role === 'user') return false  // These are system-generated prompts, not actual user input
    if (msg.role === 'assistant' && looksLikeJSON(msg.content)) return false
    return true
  }).slice(0, 1)  // Only take the first AI response (chain of thought)

  // Follow-up messages (for the Q&A section)
  const followUpMessages = allMessages.filter((msg, idx) => {
    if (msg.role === 'system') return false
    if (msg.role === 'assistant' && looksLikeJSON(msg.content)) return false
    return idx >= 3
  })

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [followUpMessages, followUpStream.content])

  const handleFollowUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!followUpQuestion.trim() || !analysisId) return
    await followUpStream.askFollowUp({ analysisId, question: followUpQuestion })
    setFollowUpQuestion('')
    refetch()
  }

  // Extract abstract from summary for Quick Summary section (must be before early returns)
  const summary = analysis?.summary
  const abstract = useMemo(() => {
    if (summary) {
      return extractAbstract(summary)
    }
    return { diagnoses: [], treatment: [] }
  }, [summary])

  // Dedupe treatment actions - simple exact match deduplication
  const dedupedTreatment = useMemo(() => {
    return [...new Set(abstract.treatment)]
  }, [abstract.treatment])

  // Get detailed analysis fields - parse JSON string if needed (ConnectRPC returns detailedAnalysisJson)
  // Must be before early returns to satisfy React hooks rules
  const detailedAnalysis = useMemo(() => {
    if (!analysis) return {}
    if (analysis.detailedAnalysis && Object.keys(analysis.detailedAnalysis).length > 0) {
      return analysis.detailedAnalysis
    }
    if (analysis.detailedAnalysisJson) {
      try {
        return JSON.parse(analysis.detailedAnalysisJson)
      } catch {
        return {}
      }
    }
    return {}
  }, [analysis])

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    )
  }

  if (error || !analysis) {
    return (
      <div className="space-y-4">
        <Link to={`/farms/${farmId}/ai/history`} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> Back to History
        </Link>
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <XCircle className="mx-auto h-8 w-8 text-red-500" />
          <p className="mt-2 font-medium text-red-800">Analysis not found</p>
        </div>
      </div>
    )
  }

  const severity = analysis.severity || 'medium'
  const severityConfig = SEVERITY_CONFIG[severity]
  const SeverityIcon = severityConfig.icon
  const confidencePercent = analysis.confidenceScore ? Math.round(analysis.confidenceScore * 100) : null
  const isRootCause = analysis.analysisType === 'root_cause'

  const createdDate = new Date(analysis.createdAt).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })

  // Extract fields from detailedAnalysis (parsed above before early returns)
  const diagnosis = detailedAnalysis.identifiedCondition || detailedAnalysis.diagnosis
  const differentialDiagnoses = detailedAnalysis.differentialDiagnosis || detailedAnalysis.differential_diagnoses || []
  const symptomsMatched = detailedAnalysis.symptoms_matched || detailedAnalysis.symptomsMatched || []
  const symptomsMissing = detailedAnalysis.symptoms_missing || detailedAnalysis.symptomsMissing || []
  const contributingFactors = detailedAnalysis.contributingFactors || []
  const treatmentProtocol = detailedAnalysis.treatmentProtocol || ''
  const preventionMeasures = detailedAnalysis.preventionMeasures || []

  // Get chain of thought from detailedAnalysis (supports both camelCase and snake_case)
  const chainOfThought = (
    detailedAnalysis.chainOfThought ||
    detailedAnalysis.chain_of_thought ||
    // Fallback: check if there's an assistant message in conversation that's not JSON
    initialMessages.find(m => m.role === 'assistant')?.content
  ) as string | undefined

  // Check if Analysis Input section has any data to display
  const hasAnyInputData = !!(
    (analysis?.imageIds && analysis.imageIds.length > 0) ||
    analysis?.triggerRecordType ||
    analysis?.lookbackDays ||
    analysis?.description ||
    analysis?.anomalyCategory ||
    analysis?.triggerMetric ||
    analysis?.inputMode ||
    analysis?.fishSpecies ||
    analysis?.affectedCount
  )

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to={`/farms/${farmId}/ai/history`}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            title="Back to History"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
              {isRootCause ? <Brain className="h-5 w-5 text-emerald-600" /> : <Fish className="h-5 w-5 text-emerald-600" />}
              {isRootCause ? 'Root Cause Analysis' : 'Fish Health Diagnosis'}
            </h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {pondName}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {createdDate}
              </span>
            </div>
          </div>
        </div>
        <RateLimitBadge />
      </div>

      {/* Analysis Input Context */}
      <div className="rounded-xl border border-slate-200/50 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-4">Analysis Input</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Images */}
          {analysis.imageIds && analysis.imageIds.length > 0 && (
            <div className="sm:col-span-2">
              <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                <ImageIcon className="h-4 w-4" />
                <span>{analysis.imageIds.length} image{analysis.imageIds.length > 1 ? 's' : ''} analyzed</span>
                {isImagesLoading && <Loader2 className="h-3 w-3 animate-spin" />}
              </div>
              {analysisImages.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {analysisImages.map((image, index) => (
                    <button
                      key={image.imageId}
                      onClick={() => {
                        setGalleryStartIndex(index)
                        setShowGallery(true)
                      }}
                      className="group relative w-24 h-24 rounded-lg overflow-hidden bg-slate-100 hover:ring-2 hover:ring-emerald-500 transition-all"
                    >
                      <img
                        src={image.signedUrl}
                        alt={image.fileName}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ImageIcon className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Root Cause specific inputs */}
          {isRootCause && (
            <>
              {analysis.triggerRecordType && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Reference Record</p>
                  <p className="text-sm font-medium text-slate-900">
                    {TRIGGER_RECORD_TYPE_LABELS[analysis.triggerRecordType] || analysis.triggerRecordType}
                  </p>
                </div>
              )}
              {analysis.lookbackDays && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Analysis Window</p>
                  <p className="text-sm font-medium text-slate-900">
                    {analysis.lookbackDays} days
                  </p>
                </div>
              )}
              {analysis.description && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-slate-500 mb-1">Problem Description</p>
                  <p className="text-sm text-slate-700">
                    {analysis.description}
                  </p>
                </div>
              )}
              {/* Legacy fields for older analyses */}
              {analysis.anomalyCategory && !analysis.triggerRecordType && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Anomaly Category</p>
                  <p className="text-sm font-medium text-slate-900">
                    {ANOMALY_CATEGORY_LABELS[analysis.anomalyCategory] || analysis.anomalyCategory}
                  </p>
                </div>
              )}
              {analysis.triggerMetric && !analysis.triggerRecordType && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Trigger Metric</p>
                  <p className="text-sm font-medium text-slate-900">
                    {analysis.triggerMetric}: {analysis.triggerValue}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Fish Diagnosis specific inputs */}
          {!isRootCause && (
            <>
              {analysis.inputMode && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Input Mode</p>
                  <p className="text-sm font-medium text-slate-900 capitalize">
                    {analysis.inputMode.replace('_', ' + ')}
                  </p>
                </div>
              )}
              {analysis.fishSpecies && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Fish Species</p>
                  <p className="text-sm font-medium text-slate-900">{analysis.fishSpecies}</p>
                </div>
              )}
              {analysis.affectedCount && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Affected Count</p>
                  <p className="text-sm font-medium text-slate-900">{analysis.affectedCount} fish</p>
                </div>
              )}
            </>
          )}

          {/* Empty state when no input data exists */}
          {!hasAnyInputData && (
            <div className="sm:col-span-2">
              <p className="text-slate-500 italic text-sm">
                This analysis was triggered automatically based on pond data patterns.
                Expand the "AI Chain of Thought" section below to see the full reasoning.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* AI Chain of Thought - Collapsible section showing the AI's diagnostic reasoning */}
      {chainOfThought && (
        <div className={`rounded-xl border ${showOriginalContext ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200/50 bg-white'} shadow-sm overflow-hidden transition-all`}>
          <button
            onClick={() => setShowOriginalContext(!showOriginalContext)}
            className="w-full p-4 flex items-center justify-between hover:bg-emerald-50 transition-all group cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 h-11 w-11 rounded-xl bg-emerald-500 flex items-center justify-center shadow-md group-hover:bg-emerald-600 transition-colors">
                <Brain className="h-6 w-6 text-white" />
              </div>
              <div className="text-left">
                <span className="font-semibold text-slate-900">AI Diagnostic Reasoning</span>
                <p className="text-xs text-slate-500">Click to {showOriginalContext ? 'hide' : 'view'} step-by-step analysis</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold px-4 py-1.5 rounded-lg transition-all ${
                showOriginalContext
                  ? 'bg-slate-200 text-slate-700'
                  : 'bg-emerald-500 text-white shadow-md group-hover:bg-emerald-600'
              }`}>
                {showOriginalContext ? 'Hide' : 'View Details'}
              </span>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
                showOriginalContext
                  ? 'bg-emerald-200'
                  : 'bg-emerald-100 group-hover:bg-emerald-200'
              }`}>
                <ChevronDown className={`w-5 h-5 transition-transform ${showOriginalContext ? 'rotate-180 text-emerald-700' : 'text-emerald-600'}`} />
              </div>
            </div>
          </button>

          {showOriginalContext && (
            <div className="px-5 pb-5 border-t border-emerald-200 pt-4 max-h-[500px] overflow-y-auto bg-white/80">
              <div className="prose prose-sm prose-slate max-w-none prose-p:my-2 prose-headings:mt-4 prose-headings:mb-2 prose-ul:my-2 prose-li:my-0.5 prose-strong:text-slate-800">
                <ReactMarkdown>{chainOfThought}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Diagnosis Result */}
      <div className="space-y-5">
        {/* Diagnosis Header */}
        <div className={`rounded-xl border-2 ${severityConfig.border} ${severityConfig.bg} overflow-hidden`}>
          <div className="p-5">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className={`rounded-full p-3 ${severity === 'critical' ? 'bg-red-100' : severity === 'high' ? 'bg-orange-100' : severity === 'medium' ? 'bg-yellow-100' : 'bg-green-100'}`}>
                  <Stethoscope className={`h-6 w-6 ${severityConfig.text}`} />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="text-xl font-semibold text-slate-900">
                    {analysis.identifiedCondition || (isRootCause ? 'Root Cause Identified' : 'Diagnosis Complete')}
                  </h3>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${severityConfig.bg} ${severityConfig.text} border ${severityConfig.border}`}>
                    <SeverityIcon className="h-3.5 w-3.5" />
                    {severityConfig.label}
                  </span>
                </div>
                {analysis.parasiteCode && (
                  <p className="mt-1 text-sm text-slate-500">
                    Reference Code: <span className="font-mono bg-white/50 px-1.5 py-0.5 rounded">{analysis.parasiteCode}</span>
                  </p>
                )}
                <p className={`mt-2 text-sm ${severityConfig.text}`}>
                  {severityConfig.description}
                </p>
              </div>
            </div>

            {/* Confidence Score */}
            {confidencePercent !== null && (
              <div className="mt-5 pt-4 border-t border-slate-200/50">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-slate-600 font-medium">Diagnostic Confidence</span>
                  <span className={`font-semibold ${confidencePercent >= 80 ? 'text-emerald-600' : confidencePercent >= 60 ? 'text-yellow-600' : 'text-orange-600'}`}>
                    {confidencePercent}%
                  </span>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-white/80">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ease-out ${confidencePercent >= 80 ? 'bg-emerald-500' : confidencePercent >= 60 ? 'bg-yellow-500' : 'bg-orange-500'}`}
                    style={{ width: `${confidencePercent}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Summary Abstract - Clean vertical layout */}
        {analysis.summary && (abstract.diagnoses.length > 0 || dedupedTreatment.length > 0) && (
          <div className="rounded-xl border border-slate-200/50 bg-gradient-to-br from-white to-slate-50/50 p-6 shadow-sm space-y-5">
            <h4 className="font-semibold text-slate-900 text-sm uppercase tracking-wide">Quick Summary</h4>

            {/* WHAT - Likely Diagnoses */}
            {abstract.diagnoses.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-emerald-700">
                  <Target className="h-4 w-4 flex-shrink-0" />
                  <span className="font-medium text-sm">Likely Diagnosis</span>
                </div>
                <div className="space-y-3">
                  {abstract.diagnoses.slice(0, 3).map((diag, idx) => (
                    <div key={idx} className="bg-slate-50/70 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                          parseInt(diag.confidence) >= 70
                            ? 'bg-emerald-100 text-emerald-700'
                            : parseInt(diag.confidence) >= 50
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-slate-100 text-slate-600'
                        }`}>
                          {diag.confidence}
                        </span>
                        <span className="text-sm text-slate-900 font-semibold">{diag.name}</span>
                      </div>
                      {diag.evidence.length > 0 && (
                        <p className="text-xs text-slate-600 leading-relaxed">{diag.evidence[0]}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Divider */}
            {abstract.diagnoses.length > 0 && dedupedTreatment.length > 0 && (
              <div className="border-t border-slate-200/70" />
            )}

            {/* HOW - Treatment (only show if not duplicated in recommendations below) */}
            {dedupedTreatment.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-purple-700">
                  <Pill className="h-4 w-4 flex-shrink-0" />
                  <span className="font-medium text-sm">Key Actions</span>
                </div>
                <div className="space-y-2">
                  {dedupedTreatment.slice(0, 3).map((action, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center mt-0.5">
                        {idx + 1}
                      </span>
                      <p className="text-sm text-slate-700 leading-relaxed">{action}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Full Analysis - Rendered Markdown */}
        {analysis.summary && (
          <div className="rounded-xl border border-slate-200/50 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <Activity className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-slate-900 mb-3">Full Analysis</h4>
                <div className="prose prose-sm prose-slate max-w-none prose-p:my-2 prose-headings:mt-4 prose-headings:mb-2 prose-ul:my-2 prose-li:my-0.5 prose-strong:text-slate-800">
                  <ReactMarkdown>{analysis.summary}</ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Root Cause specific: Primary Cause */}
        {isRootCause && detailedAnalysis.primaryCause && (
          <div className="rounded-xl border border-slate-200/50 bg-white p-5 shadow-sm">
            <h4 className="font-medium text-slate-900 mb-2">Primary Cause</h4>
            <p className="text-slate-600">{detailedAnalysis.primaryCause}</p>
            {detailedAnalysis.rootCauseChain && detailedAnalysis.rootCauseChain.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Cause Chain:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600">
                  {detailedAnalysis.rootCauseChain.map((cause: string, idx: number) => (
                    <li key={idx}>{cause}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Symptoms Analysis */}
        {(symptomsMatched.length > 0 || symptomsMissing.length > 0) && (
          <div className="rounded-xl border border-slate-200/50 bg-white p-5 shadow-sm">
            <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              Symptom Analysis
            </h4>
            <div className="grid gap-4 md:grid-cols-2">
              {symptomsMatched.length > 0 && (
                <div className="rounded-lg bg-emerald-50/50 p-4">
                  <h5 className="text-sm font-medium text-emerald-800 mb-2">Symptoms Observed</h5>
                  <ul className="space-y-1.5">
                    {symptomsMatched.map((symptom: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-emerald-700">
                        <CheckCircle className="h-4 w-4 flex-shrink-0" />
                        <span>{symptom}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {symptomsMissing.length > 0 && (
                <div className="rounded-lg bg-slate-50 p-4">
                  <h5 className="text-sm font-medium text-slate-700 mb-2">Not Observed</h5>
                  <ul className="space-y-1.5">
                    {symptomsMissing.map((symptom: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                        <span className="h-4 w-4 flex items-center justify-center text-slate-400">—</span>
                        <span>{symptom}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Differential Diagnoses */}
        {differentialDiagnoses.length > 0 && (
          <div className="rounded-xl border border-slate-200/50 bg-white p-5 shadow-sm">
            <h4 className="font-medium text-slate-900 mb-3">Other Possible Conditions</h4>
            <p className="text-sm text-slate-500 mb-3">
              While the primary diagnosis is most likely, these conditions should also be considered:
            </p>
            <div className="flex flex-wrap gap-2">
              {differentialDiagnoses.map((d: string | { condition: string; confidence_percent?: number }, idx: number) => {
                // Handle both string and object formats
                const condition = typeof d === 'string' ? d : d.condition
                const confidence = typeof d === 'object' && d.confidence_percent ? d.confidence_percent : null
                return (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-sm text-slate-700"
                  >
                    {condition}
                    {confidence !== null && (
                      <span className="text-xs text-slate-500">({confidence}%)</span>
                    )}
                  </span>
                )
              })}
            </div>
          </div>
        )}

        {/* Detailed Analysis - Collapsible */}
        {(diagnosis || contributingFactors.length > 0 || treatmentProtocol || preventionMeasures.length > 0) && (
          <div className="rounded-xl border border-slate-200/50 bg-white shadow-sm overflow-hidden">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex w-full items-center justify-between p-5 text-left hover:bg-slate-50/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-emerald-600" />
                <span className="font-medium text-slate-900">Detailed Analysis & Prevention</span>
              </div>
              {showDetails ? (
                <ChevronUp className="h-5 w-5 text-slate-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-400" />
              )}
            </button>
            {showDetails && (
              <div className="border-t border-slate-100 p-5 space-y-5">
                {diagnosis && (
                  <div>
                    <h5 className="text-sm font-medium text-slate-900 mb-2">In-Depth Diagnosis</h5>
                    <p className="text-sm text-slate-600 leading-relaxed">{diagnosis}</p>
                  </div>
                )}
                {contributingFactors.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-slate-900 mb-2">Contributing Factors</h5>
                    <ul className="space-y-2">
                      {contributingFactors.map((factor: string, idx: number) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="text-amber-500 mt-0.5">•</span>
                          {factor}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {treatmentProtocol && (
                  <div className="rounded-lg bg-blue-50/50 p-4">
                    <h5 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Treatment Protocol
                    </h5>
                    <p className="text-sm text-blue-800">{treatmentProtocol}</p>
                  </div>
                )}
                {preventionMeasures.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-slate-900 mb-2">Prevention for the Future</h5>
                    <ul className="space-y-2">
                      {preventionMeasures.map((measure: string, idx: number) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {measure}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Recommendations */}
        {analysis.recommendations && analysis.recommendations.length > 0 && (
          <div>
            <h4 className="mb-3 font-medium text-slate-900 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              Recommended Actions
            </h4>
            <RecommendationsList recommendations={analysis.recommendations} />
          </div>
        )}

        {/* Timeline Events */}
        {analysis.timelineEvents && analysis.timelineEvents.length > 0 && (
          <div>
            <h4 className="mb-3 font-medium text-slate-900">Related Events</h4>
            <Timeline events={analysis.timelineEvents} farmId={analysis.farmId} pondId={analysis.pondId} />
          </div>
        )}
      </div>

      {/* Follow-up Conversation Section */}
      <div className="rounded-xl border border-slate-200/50 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="font-medium text-slate-900 flex items-center gap-2">
            <Bot className="h-5 w-5 text-emerald-600" />
            Continue Conversation
          </h3>
          <p className="text-sm text-slate-500 mt-1">Ask follow-up questions about this analysis</p>
        </div>

        {/* Conversation Messages */}
        <div ref={chatContainerRef} className="max-h-96 overflow-y-auto p-5 space-y-4">
          {followUpMessages.length === 0 && !followUpStream.isStreaming && (
            <div className="text-center py-8 text-slate-400">
              <Bot className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No follow-up questions yet</p>
            </div>
          )}

          {followUpMessages.map((message, idx) => (
            <div key={message.id || idx} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}>
              {message.role === 'assistant' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-emerald-600" />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-100 text-slate-900'
              }`}>
                {message.role === 'assistant' ? (
                  <div className="prose prose-sm prose-slate max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-strong:text-slate-800">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                )}
              </div>
              {message.role === 'user' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                  <User className="h-4 w-4 text-slate-600" />
                </div>
              )}
            </div>
          ))}

          {/* Streaming Response */}
          {followUpStream.isStreaming && (
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <Bot className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="max-w-[80%] rounded-2xl bg-slate-100 px-4 py-3">
                {followUpStream.content ? (
                  <div className="prose prose-sm prose-slate max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-strong:text-slate-800">
                    <ReactMarkdown>{followUpStream.content}</ReactMarkdown>
                    <span className="inline-block h-4 w-1 animate-pulse bg-emerald-500 rounded ml-1" />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{followUpStream.status || 'Thinking...'}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Chat Input */}
        <div className="border-t border-slate-100 p-4">
          <form onSubmit={handleFollowUpSubmit} className="flex gap-3">
            <input
              type="text"
              value={followUpQuestion}
              onChange={(e) => setFollowUpQuestion(e.target.value)}
              placeholder="Ask a follow-up question..."
              className="flex-1 rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              disabled={followUpStream.isStreaming}
            />
            <button
              type="submit"
              disabled={!followUpQuestion.trim() || followUpStream.isStreaming}
              className="flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-3 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {followUpStream.isStreaming ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </form>
          {followUpStream.error && (
            <p className="mt-2 text-sm text-red-600">{followUpStream.error}</p>
          )}
        </div>
      </div>

      {/* Image Gallery Lightbox */}
      {showGallery && analysisImages.length > 0 && (
        <ImageGallery
          images={analysisImages.map((img) => ({
            imageId: img.imageId,
            fileName: img.fileName,
            signedUrl: img.signedUrl,
          }))}
          initialIndex={galleryStartIndex}
          onClose={() => setShowGallery(false)}
        />
      )}
    </div>
  )
}

export default AIAnalysisDetailPage
